import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  listLegalEntities,
  createLegalEntity as apiCreate,
  updateLegalEntity as apiUpdate,
  archiveLegalEntity as apiArchive
} from 'api/clientApi';
import { ARCHIVED, type LegalEntity, type LegalEntityPayload } from 'types/legal-entity';

/**
 * Entities, shared by the Entities page and the document and project forms (ADR 0003).
 *
 * Loaded on first use rather than on mount: the provider sits above the router, so loading on mount
 * would fire before sign-in and fail. `useLegalEntities()` triggers the first load itself.
 */
type LegalEntitiesContextValue = {
  entities: LegalEntity[];
  /** Non-archived only — what the association pickers offer. */
  activeEntities: LegalEntity[];
  byId: Record<string, LegalEntity>;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  ensureLoaded: () => void;
  create: (payload: LegalEntityPayload) => Promise<LegalEntity>;
  update: (id: string, payload: Partial<LegalEntityPayload> & { item_status?: number }) => Promise<LegalEntity>;
  archive: (id: string) => Promise<LegalEntity>;
};

const LegalEntitiesContext = createContext<LegalEntitiesContextValue | undefined>(undefined);

export function LegalEntitiesProvider({ children }: { children: ReactNode }) {
  const [entities, setEntities] = useState<LegalEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const list = await listLegalEntities();
      setEntities(list.sort((a, b) => a.name.localeCompare(b.name)));
      setLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load entities');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  const ensureLoaded = useCallback(() => {
    if (!loaded && !inFlight.current) void refresh();
  }, [loaded, refresh]);

  // Replace one record in place rather than refetching the whole list after every edit.
  const upsert = useCallback((entity: LegalEntity) => {
    setEntities((prev) =>
      [...prev.filter((e) => e.id !== entity.id), entity].sort((a, b) => a.name.localeCompare(b.name))
    );
    return entity;
  }, []);

  const create = useCallback(async (payload: LegalEntityPayload) => upsert(await apiCreate(payload)), [upsert]);
  const update = useCallback(
    async (id: string, payload: Partial<LegalEntityPayload> & { item_status?: number }) => upsert(await apiUpdate(id, payload)),
    [upsert]
  );
  const archive = useCallback(async (id: string) => upsert(await apiArchive(id)), [upsert]);

  const value = useMemo<LegalEntitiesContextValue>(() => {
    const activeEntities = entities.filter((e) => e.itemStatus !== ARCHIVED);
    const byId = Object.fromEntries(entities.map((e) => [e.id, e]));
    return { entities, activeEntities, byId, loading, loaded, error, refresh, ensureLoaded, create, update, archive };
  }, [entities, loading, loaded, error, refresh, ensureLoaded, create, update, archive]);

  return <LegalEntitiesContext.Provider value={value}>{children}</LegalEntitiesContext.Provider>;
}

export function useLegalEntities(): LegalEntitiesContextValue {
  const ctx = useContext(LegalEntitiesContext);
  if (!ctx) throw new Error('useLegalEntities must be used within a LegalEntitiesProvider');
  const { ensureLoaded } = ctx;
  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);
  return ctx;
}
